import {
  AppAdminFormLabel,
  AppAlert,
  AppCheckbox,
  AppDivider,
  AppTextField,
} from '@/components/atoms';
import { DEFAULT_SITES } from '@/constants/routeSegments';
import { SSO_SYSTEMS, type AdminUserFormData } from '@/schemas/adminUserSchema';
import React from 'react';
import {
  Controller,
  useController,
  useWatch,
  type Control,
  type FieldErrors,
} from 'react-hook-form';

interface AdminUserSsoFieldsProps {
  control: Control<AdminUserFormData>;
  errors: FieldErrors<AdminUserFormData>;
  /** `manage_site` currently selected in the form. Empty until a super admin picks one. */
  manageSite: string;
}

const SYSTEM_LABELS: Record<(typeof SSO_SYSTEMS)[number], string> = {
  P2: '審査システム',
  P3: '開示システム',
};

/**
 * SSO連携 block, appended to the 管理者作成 / 管理者編集 form.
 *
 * Kept as its own component so the existing layout is untouched: 管理者名, メールアドレス and
 * パスワード keep their position and their markup.
 *
 * One ログインID is shared by every ticked system. The field is always rendered and only
 * disabled while nothing is ticked -- hiding it would leave no sign that a step remains.
 *
 * Hiding this block for a super admin is a convenience, not a rule: react-hook-form keeps the
 * values of unmounted fields, so the request still carries them. The rule itself lives in
 * ValidatesAdminSsoGrant on the backend.
 */
const AdminUserSsoFields: React.FC<AdminUserSsoFieldsProps> = ({
  control,
  errors,
  manageSite,
}) => {
  // Read-only view of the field the checkboxes write. A second <Controller> would register the
  // same field twice for what is only a read.
  const selectedSystems = useWatch({ control, name: 'sso_systems' }) ?? [];
  const linked = selectedSystems.length > 0;

  // Registered here rather than only inside its <Controller> so unticking the last system can
  // blank the value: BD 2.0/3.0 asks for the field to be disabled *and* cleared, and a disabled
  // input keeps whatever it held. The backend blanks it too (ValidatesAdminSsoGrant), but that
  // leaves the user looking at an ID the request no longer sends.
  const { field: loginIdField } = useController({ control, name: 'sso_login_id' });

  // 審査システム (P2) is a MEDIX-ONLY deployment: its site catalogue holds one row (tenant medix)
  // and the broker client registered for it declares a single site, `medix/A2`. Ticking it for any
  // other サイト区分 cannot succeed, and it fails QUIETLY -- the account row still reaches
  // `m_tantosha` through Common Sync and only the identity link is refused, into a log line. The
  // grant then reads as active on this screen for a link that was never written.
  //
  // Greying out is the visible half; AdminUserForm drops a stale tick, and ValidatesAdminSsoGrant
  // refuses the combination outright, because a disabled field still submits its value.
  const p2Available = manageSite === DEFAULT_SITES.MEDIX;

  // BD section 2: the block only shows once サイト区分 is something other than スーパー管理者.
  if (manageSite === '' || manageSite === DEFAULT_SITES.SUPER_ADMIN) {
    return null;
  }

  return (
    <>
      <AppDivider className="my-2" />

      <div>
        <p className="text-sm font-normal mb-1">SSO連携</p>
        <p className="text-xs text-gray-500 mb-3">
          連携するシステムにチェックを入れてください。
        </p>

        <Controller
          name="sso_systems"
          control={control}
          render={({ field }) => {
            const selected = field.value ?? [];

            const toggle = (system: (typeof SSO_SYSTEMS)[number], checked: boolean) => {
              const next = checked
                ? [...selected, system]
                : selected.filter(item => item !== system);

              field.onChange(next);

              if (next.length === 0) {
                loginIdField.onChange('');
              }
            };

            return (
              <div className="flex flex-col gap-2">
                {SSO_SYSTEMS.map(system => {
                  const disabled = system === 'P2' && !p2Available;

                  return (
                    <div key={system} className="flex items-center h-10 border rounded px-4">
                      <AppCheckbox
                        checked={selected.includes(system)}
                        disabled={disabled}
                        onChange={event => toggle(system, event.target.checked)}
                      >
                        {SYSTEM_LABELS[system]}
                      </AppCheckbox>

                      {disabled && (
                        <span data-testid="sso-p2-unavailable" className="text-xs text-gray-500 ml-2">
                          メディックスのみ
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            );
          }}
        />

        {errors.sso_systems && (
          <p className="text-red-500 text-sm mt-1">{errors.sso_systems.message}</p>
        )}
      </div>

      <div>
        <AppAdminFormLabel label="ログインID" required />
        <AppTextField
          {...loginIdField}
          value={loginIdField.value ?? ''}
          className="w-full"
          disabled={!linked}
          inputMode="numeric"
          maxLength={6}
          status={errors.sso_login_id ? 'error' : undefined}
        />

        {errors.sso_login_id && (
          <p className="text-red-500 text-sm mt-1">{errors.sso_login_id.message}</p>
        )}

        {/*
          Luôn hiển thị, KHÔNG nằm ở nhánh else của lỗi.

          Đây là mô tả định dạng theo BD sheet 2 (mục 4.0), không phải thông báo lỗi. Trước đây nó
          là nhánh else của ternary nên biến mất đúng lúc người dùng cần nó nhất: gõ sai định dạng
          → lỗi hiện ra → dòng nói cho họ biết định dạng ĐÚNG là gì thì lại vừa bị ẩn đi.
        */}
        <p data-testid="sso-login-id-help" className="text-xs text-gray-500 mt-1">
          6桁以内の数字を入力してください。審査・開示システム共有です。
        </p>

        <AppAlert
          type="info"
          className="mt-3"
          style={{ paddingTop: 8, paddingBottom: 8 }}
          message={
            <span className="text-xs">
              パスワードは上記「パスワード」と共通です。SSO連携用に別途設定する必要はありません。
            </span>
          }
        />
      </div>
    </>
  );
};

export default AdminUserSsoFields;
